import { formatCurrency, fromDateInput, startOfMonth, toDateInput } from '@crm/shared';
import { Button, Card, CardContent, Input, Select, Spinner } from '@crm/ui';
import { HandCoins, Scissors, Wallet } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, ExportPdfButton, PageHeader, PrintButton, StatCard } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { useSession } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import { useProfessionals } from '@/modules/schedule/api';
import { COMMISSIONS_ROUTES, useCommissionReport } from '../api';

/** Apuracao das comissoes: quanto cada profissional produziu e vai receber. */
export function CommissionReportPage() {
  useDocumentTitle('Comissoes');

  const navigate = useNavigate();
  const { user } = useSession();
  const units = user?.units ?? [];
  const professionals = useProfessionals();

  const [from, setFrom] = useState(() => toDateInput(startOfMonth(new Date())));
  const [to, setTo] = useState(() => toDateInput(new Date()));
  const [professionalId, setProfessionalId] = useState('');
  const [unitId, setUnitId] = useState('');

  const report = useCommissionReport({
    from: fromDateInput(from).toISOString(),
    to: fromDateInput(to, true).toISOString(),
    professionalId: professionalId || undefined,
    unitId: unitId || undefined,
  });

  const totals = report.data?.totals;
  const rows = report.data?.byProfessional ?? [];
  // A barra compara os profissionais entre si: o maior enche a linha toda.
  const topCommission = Math.max(...rows.map((row) => row.commission), 0);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<HandCoins />}
        title="Comissoes"
        description="O que cada profissional produziu no periodo e quanto ganha sobre isso."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <PrintButton />
            <ExportPdfButton title="Comissoes a pagar" />
            <Button variant="outline" onClick={() => navigate(COMMISSIONS_ROUTES.RULES)}>
              Regras de comissao
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
        <StatCard
          title="Comissao do periodo"
          value={formatCurrency(totals?.commission ?? 0)}
          hint="Total a pagar aos profissionais"
          icon={<HandCoins />}
        />
        <StatCard
          title="Producao"
          value={formatCurrency(totals?.revenue ?? 0)}
          hint="Servicos finalizados, ja com desconto"
          icon={<Wallet />}
        />
        <StatCard
          title="Servicos"
          value={totals?.services ?? 0}
          hint="Atendimentos concluidos no periodo"
          icon={<Scissors />}
          className="col-span-2 sm:col-span-1"
        />
      </div>

      <Card variant="flush">
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex items-center gap-2">
              <Input
                type="date"
                aria-label="De"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
              />
              <span className="text-sm text-muted-foreground">a</span>
              <Input
                type="date"
                aria-label="Ate"
                value={to}
                onChange={(event) => setTo(event.target.value)}
              />
            </div>

            <Select
              aria-label="Profissional"
              value={professionalId}
              onChange={(event) => setProfessionalId(event.target.value)}
            >
              <option value="">Todos os profissionais</option>
              {(professionals.data?.data ?? []).map((professional) => (
                <option key={professional.id} value={professional.id}>
                  {professional.name}
                </option>
              ))}
            </Select>

            {units.length > 1 ? (
              <Select
                aria-label="Unidade"
                value={unitId}
                onChange={(event) => setUnitId(event.target.value)}
              >
                <option value="">Todas as unidades</option>
                {units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name}
                  </option>
                ))}
              </Select>
            ) : null}
          </div>

          {report.isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-6 w-6 text-muted-foreground" />
            </div>
          ) : report.isError ? (
            <p className="text-sm text-destructive">{toErrorMessage(report.error)}</p>
          ) : rows.length > 0 ? (
            <ul className="divide-y divide-border">
              {rows.map((row) => (
                <li key={row.professionalId ?? 'sem-profissional'} className="py-3 first:pt-0">
                  <div className="flex items-center gap-3">
                    <Avatar name={row.professionalName} className="h-10 w-10 shrink-0" />

                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{row.professionalName}</p>
                      <p className="text-xs text-muted-foreground">
                        {row.services} servico{row.services === 1 ? '' : 's'} -{' '}
                        {formatCurrency(row.revenue)} de producao
                      </p>
                    </div>

                    <p className="shrink-0 font-semibold text-emerald-700 dark:text-emerald-400">
                      {formatCurrency(row.commission)}
                    </p>
                  </div>

                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-primary"
                      style={{
                        width: `${topCommission > 0 ? (row.commission / topCommission) * 100 : 0}%`,
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Nenhum atendimento finalizado no periodo.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
